// The Earth away from the Cape: where the sea is, how the land lies and what
// it looks like from a few kilometres up to the ground.
//
// Near the Cape the ground is real imagery over real coastlines (coast.js).
// Everywhere else the only data is the app's day map, about 4.9 km a texel.
// This module reads that map the same way twice: on the CPU from the texture's
// own canvas, a tile at a time, and on the GPU with texelFetch on the same
// texture. Both get the same answer, so the mesh, the physics and the shading
// agree on:
//
//  - Where the sea is. The map paints ocean and lakes one flat colour (sRGB
//    30, 59, 117 covers 97% of open-ocean texels), so a texel is sea or land
//    by its distance from that colour, and the class is read bilinearly. The
//    coastline is the half contour of that field, moved about by a few hundred
//    metres of fractal noise so it reads as a coast instead of a 5 km polygon.
//    The noise is an integer hash both sides compute bit for bit.
//  - How far the nearest land is, from the near-coast gradient up to about
//    80 km through the map's own mip chain. The water shading in terrain.js
//    reads that distance for its surf, its shelf and its open-ocean colour.
//  - How high the land is: rolling relief of a few tens of metres, flattening
//    toward the shore and over ice.
//
// The shading below is GPU only: land cover taken from the map's colour
// (forest, grass and crops, dry scrub, desert sand, rock, snow and ice) with
// procedural detail in world space. Every procedural pattern is periodic in a
// power of two of metres, which is what lets a patch carry its texture frame
// to a new centre and fold the offset back toward zero without the ground
// moving under the player (TerrainPatch.recentre).

import { KSC_LEVELS } from './kscLevels.js';

const DEG_M = 6371000 * Math.PI / 180;      // metres per degree of latitude
const R_EARTH = 6371000;

// The day map's ocean colour, and how far a texel may stray from it and still
// count as water (JPEG ringing along coasts is a few levels).
const SEA_RGB = [30, 59, 117];
const SEA_NEAR = 6, SEA_FAR = 22;

// Fractal coastline. Cell sizes are 360 / (256 * n) degrees so that a whole
// turn of longitude is a whole number of hash periods and the noise meets
// itself at the date line. Amplitudes are metres of shoreline displacement.
const COAST_N = [47, 141, 423, 1269, 3807];
const COAST_AMP = [330, 135, 55, 22, 8];
const COAST_MASK = 255;                      // hash period, in cells
const COAST_INV = COAST_N.map(n => 256 * n / 360);          // cells per degree
const COAST_M = COAST_N.map(n => 360 / (256 * n) * DEG_M);  // cell size, metres

// Where d stops being the linearised distance to the contour and saturates.
const D_MAX = 1500;

// Rolling relief: wavelength (m) and amplitude (m). Nothing finer than a few
// hundred metres, so the mesh's 19.5 m core cells carry all of it and physics
// lands on what you see.
const RELIEF = [[20000, 30], [6800, 17], [2300, 8], [800, 3], [280, 1.1]];
const RELIEF_LIFT = 14;      // mean rise of the land above the plain (m)

// --- The day map on the CPU ------------------------------------------------

const T = 64;                // tile size, texels
let img = null, imgW = 0, imgH = 0, imgCtx = null, scratch = null, scratchCtx = null;
const tiles = new Map();
const TILE_CAP = 420;
let lastKey = -1, lastTile = null;

// Called by the first Earth terrain patch with the body's day texture.
export function setDayTexture(texture) {
  const image = texture && texture.image;
  if (!image || image === img) return;
  const w = image.naturalWidth || image.videoWidth || image.width || 0;
  const h = image.naturalHeight || image.videoHeight || image.height || 0;
  if (!w || !h) return;
  img = image; imgW = w; imgH = h;
  tiles.clear(); lastKey = -1; lastTile = null;
  imgCtx = null;
  try {
    if (typeof image.getContext === 'function') imgCtx = image.getContext('2d', { willReadFrequently: true });
  } catch (err) { imgCtx = null; }
  if (!imgCtx && typeof document !== 'undefined') {
    scratch = document.createElement('canvas');
    scratch.width = T; scratch.height = T;
    scratchCtx = scratch.getContext('2d', { willReadFrequently: true });
  }
}

export function dayMapReady() { return !!img; }
// Never zero: the shader divides by this and takes a modulo of it.
export function dayMapSize() { return { w: imgW || 1, h: imgH || 1 }; }

// Sea class of one texel colour: 1 water, 0 land.
function seaOf(r, g, b) {
  const d = Math.hypot(r - SEA_RGB[0], g - SEA_RGB[1], b - SEA_RGB[2]);
  const t = Math.min(1, Math.max(0, (d - SEA_NEAR) / (SEA_FAR - SEA_NEAR)));
  return 1 - t * t * (3 - 2 * t);
}

// Ice class: bright and colourless is snow, ice cap or sea ice.
function iceOf(r, g, b) {
  const lo = Math.min(r, g, b), hi = Math.max(r, g, b);
  const bright = Math.min(1, Math.max(0, (lo - 185) / 30));
  const grey = 1 - Math.min(1, Math.max(0, (hi - lo - 10) / 18));
  return bright * bright * (3 - 2 * bright) * grey;
}

// One tile of the day map, in image rows (row 0 is the north edge).
function getTile(tx, ty) {
  const key = ty * 4096 + tx;
  if (key === lastKey) return lastTile;
  let t = tiles.get(key);
  if (!t) {
    const x0 = tx * T, y0 = ty * T;
    const w = Math.min(T, imgW - x0), h = Math.min(T, imgH - y0);
    let px = null;
    try {
      if (imgCtx) px = imgCtx.getImageData(x0, y0, w, h).data;
      else if (scratchCtx) { scratchCtx.clearRect(0, 0, T, T); scratchCtx.drawImage(img, x0, y0, w, h, 0, 0, w, h); px = scratchCtx.getImageData(0, 0, w, h).data; }
    } catch (err) { px = null; }
    const sea = new Float32Array(T * T), ice = new Float32Array(T * T);
    if (px) {
      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          const i = (y * w + x) * 4;
          const k = y * T + x;
          sea[k] = seaOf(px[i], px[i + 1], px[i + 2]);
          ice[k] = iceOf(px[i], px[i + 1], px[i + 2]);
        }
      }
    }
    t = { sea, ice, f: null };
    tiles.set(key, t);
    if (tiles.size > TILE_CAP) { const first = tiles.keys().next().value; tiles.delete(first); }
  }
  lastKey = key; lastTile = t;
  return t;
}

// Sea class at a texel, x wrapped, image row clamped.
function seaTexel(x, row) {
  const ix = ((x % imgW) + imgW) % imgW;
  const iy = row < 0 ? 0 : row >= imgH ? imgH - 1 : row;
  const t = getTile(ix >> 6, iy >> 6);
  return t.sea[(iy & 63) * T + (ix & 63)];
}

function iceTexel(x, row) {
  const ix = ((x % imgW) + imgW) % imgW;
  const iy = row < 0 ? 0 : row >= imgH ? imgH - 1 : row;
  const t = getTile(ix >> 6, iy >> 6);
  return t.ice[(iy & 63) * T + (ix & 63)];
}

// Mean sea class over 5 x 5 texels, per texel, cached with its tile. This is
// the only thing that says "the shore is near" over kilometres rather than
// metres, and it is smooth, which the linearised distance is not.
function nearTexel(x, row) {
  const ix = ((x % imgW) + imgW) % imgW;
  const iy = row < 0 ? 0 : row >= imgH ? imgH - 1 : row;
  const t = getTile(ix >> 6, iy >> 6);
  if (!t.f) {
    const f = new Float32Array(T * T);
    const x0 = (ix >> 6) * T, y0 = (iy >> 6) * T;
    const row0 = new Float32Array((T + 4) * (T + 4));
    for (let y = 0; y < T + 4; y++) {
      for (let x2 = 0; x2 < T + 4; x2++) row0[y * (T + 4) + x2] = seaTexel(x0 + x2 - 2, y0 + y - 2);
    }
    const tmp = new Float32Array((T + 4) * T);
    for (let y = 0; y < T + 4; y++) {
      for (let x2 = 0; x2 < T; x2++) {
        let s = 0;
        for (let k = 0; k < 5; k++) s += row0[y * (T + 4) + x2 + k];
        tmp[y * T + x2] = s;
      }
    }
    for (let y = 0; y < T; y++) {
      for (let x2 = 0; x2 < T; x2++) {
        let s = 0;
        for (let k = 0; k < 5; k++) s += tmp[(y + k) * T + x2];
        f[y * T + x2] = s / 25;
      }
    }
    t.f = f;
  }
  return t.f[(iy & 63) * T + (ix & 63)];
}

// --- Shared noise (the GLSL below computes the same numbers) ---------------

function hash2(x, y, seed) {
  let h = (Math.imul(x, 0x27d4eb2d) ^ Math.imul(y, 0x165667b1) ^ Math.imul(seed, 0x9e3779b9)) | 0;
  h = Math.imul(h ^ (h >>> 15), 0x85ebca6b) | 0;
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35) | 0;
  h ^= h >>> 16;
  return (h >>> 8) / 16777216;
}

function vnoise(qx, qy, mask, seed) {
  const fx0 = Math.floor(qx), fy0 = Math.floor(qy);
  const fx = qx - fx0, fy = qy - fy0;
  const ux = fx * fx * (3 - 2 * fx), uy = fy * fy * (3 - 2 * fy);
  const x0 = fx0 & mask, x1 = (fx0 + 1) & mask, y0 = fy0 & mask, y1 = (fy0 + 1) & mask;
  const a = hash2(x0, y0, seed), b = hash2(x1, y0, seed);
  const c = hash2(x0, y1, seed), d = hash2(x1, y1, seed);
  const ab = a + (b - a) * ux, cd = c + (d - c) * ux;
  return ab + (cd - ab) * uy;
}

// Shoreline displacement (m) at a geodetic position.
function coastNoise(latDeg, lonDeg) {
  let n = 0;
  for (let k = 0; k < COAST_N.length; k++) {
    const inv = COAST_INV[k];
    n += (vnoise(lonDeg * inv, latDeg * inv, COAST_MASK, 11 + k) * 2 - 1) * COAST_AMP[k];
  }
  return n;
}

// --- Value noise on the sphere, for relief --------------------------------

function hash3(x, y, z) {
  const h = Math.sin(x * 127.1 + y * 311.7 + z * 74.7) * 43758.5453123;
  return h - Math.floor(h);
}
function smooth(t) { return t * t * (3 - 2 * t); }
function noise3(x, y, z) {
  const ix = Math.floor(x), iy = Math.floor(y), iz = Math.floor(z);
  const fx = smooth(x - ix), fy = smooth(y - iy), fz = smooth(z - iz);
  const lerp = (a, b, t) => a + (b - a) * t;
  const c000 = hash3(ix, iy, iz), c100 = hash3(ix + 1, iy, iz);
  const c010 = hash3(ix, iy + 1, iz), c110 = hash3(ix + 1, iy + 1, iz);
  const c001 = hash3(ix, iy, iz + 1), c101 = hash3(ix + 1, iy, iz + 1);
  const c011 = hash3(ix, iy + 1, iz + 1), c111 = hash3(ix + 1, iy + 1, iz + 1);
  return lerp(
    lerp(lerp(c000, c100, fx), lerp(c010, c110, fx), fy),
    lerp(lerp(c001, c101, fx), lerp(c011, c111, fx), fy),
    fz
  ) * 2 - 1;
}

// --- The Cape's region box -------------------------------------------------

const REGION = KSC_LEVELS.region;

// True inside the box the Cape's coastline data covers, where terrain.js
// keeps its own model untouched.
export function inCapeRegion(latDeg, lonDeg) {
  const dLon = ((lonDeg - REGION.lonMin + 540) % 360) - 180;
  return latDeg > REGION.latMin && latDeg < REGION.latMax
    && dLon > 0 && dLon < REGION.lonMax - REGION.lonMin;
}

// Metres outside the box, 0 on its edge and inside.
function outsideRegion(latDeg, lonDeg) {
  const dLat = Math.max(REGION.latMin - latDeg, latDeg - REGION.latMax, 0) * DEG_M;
  const cl = Math.cos(latDeg * Math.PI / 180);
  const w = ((lonDeg - REGION.lonMin + 540) % 360) - 180;
  const span = REGION.lonMax - REGION.lonMin;
  const dLon = Math.max(-w, w - span, 0) * DEG_M * cl;
  return Math.hypot(dLat, dLon);
}

// --- The far field, for terrain.js ----------------------------------------

const _far = { d: 0, sea: 0, near: 0, ice: 0, ok: false };

// Signed distance to the coastline (m, land positive) and the fields the
// height model needs, at a geodetic position in degrees. `ok` is false
// wherever the day map has not loaded, which is every headless test.
export function farCoast(latDeg, lonDeg, out = _far) {
  out.ok = false; out.d = D_MAX; out.sea = 0; out.near = 0; out.ice = 0;
  if (!img) return out;
  const x = (lonDeg + 180) / 360 * imgW - 0.5;
  const y = (90 - latDeg) / 180 * imgH - 0.5;       // image rows run north to south
  const i0 = Math.floor(x), j0 = Math.floor(y);
  const fx = x - i0, fy = y - j0;
  const s00 = seaTexel(i0, j0), s10 = seaTexel(i0 + 1, j0);
  const s01 = seaTexel(i0, j0 + 1), s11 = seaTexel(i0 + 1, j0 + 1);
  const top = s00 + (s10 - s00) * fx, bot = s01 + (s11 - s01) * fx;
  const s = top + (bot - top) * fy;
  // Gradient of that bilinear patch, per texel, then per metre. Rows run
  // south as y grows, so the y gradient is negated to point north.
  const gx = (s10 - s00) * (1 - fy) + (s11 - s01) * fy;
  const gy = -((s01 - s00) * (1 - fx) + (s11 - s10) * fx);
  const mx = (360 / imgW) * DEG_M * Math.cos(latDeg * Math.PI / 180);
  const my = (180 / imgH) * DEG_M;
  const g = Math.hypot(gx / mx, gy / my);
  let d = (0.5 - s) / Math.max(g, 1e-9);
  d = d < -D_MAX ? -D_MAX : d > D_MAX ? D_MAX : d;
  const taper = 1 - smooth(Math.min(1, Math.max(0, (Math.abs(d) - 1000) / 500)));
  if (taper > 0) d += taper * coastNoise(latDeg, lonDeg);
  const n00 = nearTexel(i0, j0), n10 = nearTexel(i0 + 1, j0);
  const n01 = nearTexel(i0, j0 + 1), n11 = nearTexel(i0 + 1, j0 + 1);
  const nt = n00 + (n10 - n00) * fx, nb = n01 + (n11 - n01) * fx;
  const c00 = iceTexel(i0, j0), c10 = iceTexel(i0 + 1, j0);
  const c01 = iceTexel(i0, j0 + 1), c11 = iceTexel(i0 + 1, j0 + 1);
  const it = c00 + (c10 - c00) * fx, ib = c01 + (c11 - c01) * fx;
  out.d = d;
  out.sea = s;
  out.near = nt + (nb - nt) * fy;
  out.ice = it + (ib - it) * fy;
  out.ok = true;
  return out;
}

// Height (m) of the land away from the Cape's region, given the plain height
// terrain.js has already built. `dir` is the unit direction of the point.
// minFeature drops relief a ring cannot draw, exactly as the plain does.
export function farHeight(plain, latDeg, lonDeg, dx, dy, dz, minFeature) {
  const c = farCoast(latDeg, lonDeg);
  if (c.ok && c.d <= 0) return 0;
  // Relief fades in over 60 km outside the Cape's box, so the two models meet
  // at the plain rather than at a step.
  const edge = smooth(Math.min(1, outsideRegion(latDeg, lonDeg) / 60e3));
  if (edge <= 0) return plain;
  // Some country is flat and some is not: a slow roughness field, the sea's
  // own flattening near the shore, and ice, which lies smooth.
  const rough = 0.35 + 0.65 * smooth(Math.min(1, Math.max(0, noise3(dx * 90 + 3.1, dy * 90 + 7.9, dz * 90 + 1.3) * 0.9 + 0.55)));
  const shore = c.ok ? 1 - smooth(Math.min(1, Math.max(0, (c.near - 0.02) / 0.38))) : 1;
  const ice = c.ok ? 1 - 0.72 * c.ice : 1;
  const amp = edge * rough * shore * ice;
  let relief = 0;
  for (const [wavelength, a] of RELIEF) {
    if (wavelength < minFeature * 0.5) continue;
    const f = R_EARTH / wavelength;
    relief += noise3(dx * f + 41.7, dy * f + 12.3, dz * f + 27.1) * a;
  }
  let h = plain + amp * (relief + RELIEF_LIFT);
  // The last few tens of metres to the waterline.
  if (c.ok) h *= smooth(Math.min(1, c.d / 60));
  return h;
}

// True over sea, lake or river away from the Cape's region. Null where the
// day map has not loaded, so the caller can fall back to the app's mask.
export function farWater(latDeg, lonDeg) {
  if (!img) return null;
  return farCoast(latDeg, lonDeg).d < 0;
}

// Per-octave lattice offsets of a patch centre, folded into the hash period so
// the shader can add its own small offsets in float and land on the same
// lattice cell the CPU does.
export function coastLatticeOffsets(latDeg, lonDeg) {
  const out = [];
  for (let k = 0; k < COAST_N.length; k++) {
    const inv = COAST_INV[k];
    const qx = lonDeg * inv, qy = latDeg * inv;
    const P = COAST_MASK + 1;
    out.push([qx - P * Math.floor(qx / P), qy - P * Math.floor(qy / P)]);
  }
  return out;
}

export const FAR_CONST = { D_MAX, SEA_RGB, SEA_NEAR, SEA_FAR, COAST_INV, COAST_AMP, COAST_M, COAST_MASK, DEG_M };

// ---------------------------------------------------------------------------
// GLSL
// ---------------------------------------------------------------------------

const f4 = (x) => x.toFixed(4);
const f7 = (x) => x.toFixed(7);

// Declarations and helpers, added to the Earth fragment shader of a roaming
// patch. Everything is prefixed fx so nothing collides with the Cape's own
// c-prefixed locals or the water chunks.
export const FAR_PARS = /* glsl */`
uniform vec4 uFarM;          // texture frame rotation, as two columns
uniform vec2 uFarO;          // its fine offset (m), folded near zero
uniform vec2 uFarOB;         // and the coarse offset for kilometre patterns
uniform vec2 uFarTexI;       // day map texel of the patch centre, whole part
uniform vec2 uFarTexF;       // and fraction
uniform vec2 uFarTexScale;   // day map texels per degree
uniform vec2 uFarTexSize;    // day map size in texels
uniform float uFarLat0;      // patch centre latitude, degrees
uniform vec2 uFarCoastQ[5];  // coastline noise lattice offsets of the centre
uniform vec2 uFarWind;       // prevailing wind in the texture frame

const vec3 FX_SEA_RGB = vec3(${SEA_RGB[0]}.0, ${SEA_RGB[1]}.0, ${SEA_RGB[2]}.0);
const float FX_DEG_M = ${f4(DEG_M)};

// One 32-bit integer hash, the same arithmetic the CPU runs.
float fxHash(ivec2 c, int seed) {
  uvec2 u = uvec2(c);
  uint h = (u.x * 0x27d4eb2du) ^ (u.y * 0x165667b1u) ^ (uint(seed) * 0x9e3779b9u);
  h = (h ^ (h >> 15u)) * 0x85ebca6bu;
  h = (h ^ (h >> 13u)) * 0xc2b2ae35u;
  h ^= h >> 16u;
  return float(h >> 8u) * (1.0 / 16777216.0);
}

// Value noise on a lattice that repeats every (mask + 1) cells, so a patch can
// fold its texture offset without the pattern moving.
float fxNoise(vec2 q, int mask, int seed) {
  vec2 fl = floor(q);
  ivec2 i = ivec2(fl);
  vec2 f = q - fl;
  vec2 u = f * f * (3.0 - 2.0 * f);
  float a = fxHash(i & mask, seed);
  float b = fxHash((i + ivec2(1, 0)) & mask, seed);
  float c = fxHash((i + ivec2(0, 1)) & mask, seed);
  float d = fxHash((i + ivec2(1, 1)) & mask, seed);
  return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
}

// The same, with its gradient in cell units (value, d/dx, d/dy).
vec3 fxNoiseD(vec2 q, int mask, int seed) {
  vec2 fl = floor(q);
  ivec2 i = ivec2(fl);
  vec2 f = q - fl;
  vec2 u = f * f * (3.0 - 2.0 * f);
  vec2 du = 6.0 * f * (1.0 - f);
  float a = fxHash(i & mask, seed);
  float b = fxHash((i + ivec2(1, 0)) & mask, seed);
  float c = fxHash((i + ivec2(0, 1)) & mask, seed);
  float d = fxHash((i + ivec2(1, 1)) & mask, seed);
  float k1 = b - a, k2 = c - a, k3 = a - b - c + d;
  return vec3(a + k1 * u.x + k2 * u.y + k3 * u.x * u.y,
    du.x * (k1 + k3 * u.y), du.y * (k2 + k3 * u.x));
}

// Nearest jittered lattice point: (distance, second distance, its hash), with
// the offset to it in cell units.
vec4 fxCell(vec2 q, int mask, int seed, out vec2 toNear) {
  vec2 fl = floor(q);
  ivec2 i = ivec2(fl);
  vec2 f = q - fl;
  float d1 = 9.0, d2 = 9.0, id = 0.0;
  toNear = vec2(0.0);
  for (int y = -1; y <= 1; y++) {
    for (int x = -1; x <= 1; x++) {
      ivec2 c = (i + ivec2(x, y)) & mask;
      vec2 o = vec2(float(x), float(y)) + vec2(fxHash(c, seed), fxHash(c, seed + 7)) * 0.72 + 0.14 - f;
      float dd = dot(o, o);
      if (dd < d1) { d2 = d1; d1 = dd; id = fxHash(c, seed + 13); toNear = o; }
      else if (dd < d2) { d2 = dd; }
    }
  }
  return vec4(sqrt(d1), sqrt(d2), id, 0.0);
}

// Sea class of one day-map colour, measured in 8-bit sRGB.
float fxSeaOf(vec3 lin) {
  vec3 s = mix(lin * 12.92, 1.055 * pow(max(lin, vec3(0.0)), vec3(1.0 / 2.4)) - 0.055, step(vec3(0.0031308), lin)) * 255.0;
  return 1.0 - smoothstep(${f4(SEA_NEAR)}, ${f4(SEA_FAR)}, distance(s, FX_SEA_RGB));
}

`;

// Goes in after the map's own sampler is declared, which is later in the
// shader than the block above.
export const FAR_MAP_PARS = /* glsl */`
#ifdef USE_MAP
// How much land there is within a few tens of kilometres, read off the day
// map's own mip chain: the ocean is one flat colour, so any land in the box
// pulls the average away from it.
float fxLandNear(vec2 uv, float lod) {
  vec3 lin = textureLod(map, uv, lod).rgb;
  vec3 s = mix(lin * 12.92, 1.055 * pow(max(lin, vec3(0.0)), vec3(1.0 / 2.4)) - 0.055, step(vec3(0.0031308), lin)) * 255.0;
  return smoothstep(3.0, 45.0, distance(s, FX_SEA_RGB));
}
#endif
`;

// The texture frame and the day-map address of this fragment. Runs at the top
// of the Earth fragment body, before anything reads cTP. The frame is carried
// from patch to patch and folded back toward zero, so the ground does not move
// when the patch recentres and nothing loses float precision far from home.
export const FAR_FRAME = /* glsl */`
mat2 fxM = mat2(uFarM.xy, uFarM.zw);
vec2 cTP = fxM * vLocalPos.xz + uFarO;
vec2 cTPB = fxM * vLocalPos.xz + uFarOB;
float cFarLat = uFarLat0 + vGeo.y;
vec2 cFarTexel = uFarTexI + uFarTexF + vGeo * uFarTexScale * vec2(1.0, -1.0);
vec2 cFarUv = vec2(fract((cFarTexel.x + 0.5) / uFarTexSize.x), 1.0 - (cFarTexel.y + 0.5) / uFarTexSize.y);
vec2 cLSlope = vec2(0.0);
vec3 cFarDbg = vec3(0.0);
`;

// The coastline and the distance to it. cFarD is metres, land positive: the
// contour of the day map's own water class, moved by fractal noise near the
// shore and handed over to the mip chain's reading of the same map beyond a
// kilometre or so, out to about 80 km.
export const FAR_COAST = /* glsl */`
float cFarD = ${f4(D_MAX)};
float cFarSea = 0.0;
#ifdef USE_MAP
{
  ivec2 fxSz = ivec2(uFarTexSize);
  vec2 fxFl = floor(cFarTexel);
  vec2 fxFr = cFarTexel - fxFl;
  ivec2 fxI = ivec2(fxFl);
  int fxX0 = (fxI.x + fxSz.x) % fxSz.x;
  int fxX1 = (fxI.x + 1 + fxSz.x) % fxSz.x;
  int fxY0 = clamp(fxSz.y - 1 - fxI.y, 0, fxSz.y - 1);
  int fxY1 = clamp(fxSz.y - 2 - fxI.y, 0, fxSz.y - 1);
  float s00 = fxSeaOf(texelFetch(map, ivec2(fxX0, fxY0), 0).rgb);
  float s10 = fxSeaOf(texelFetch(map, ivec2(fxX1, fxY0), 0).rgb);
  float s01 = fxSeaOf(texelFetch(map, ivec2(fxX0, fxY1), 0).rgb);
  float s11 = fxSeaOf(texelFetch(map, ivec2(fxX1, fxY1), 0).rgb);
  float fxTop = mix(s00, s10, fxFr.x), fxBot = mix(s01, s11, fxFr.x);
  cFarSea = mix(fxTop, fxBot, fxFr.y);
  vec2 fxG = vec2(mix(s10 - s00, s11 - s01, fxFr.y), mix(s01 - s00, s11 - s10, fxFr.x));
  vec2 fxMet = vec2(FX_DEG_M * cos(radians(cFarLat)) / uFarTexScale.x, FX_DEG_M / uFarTexScale.y);
  float fxGm = length(fxG / fxMet);
  cFarD = clamp((0.5 - cFarSea) / max(fxGm, 1e-9), -${f4(D_MAX)}, ${f4(D_MAX)});
  float fxTaper = 1.0 - smoothstep(1000.0, 1500.0, abs(cFarD));
  if (fxTaper > 0.0) {
    float fxN = 0.0;
    ${COAST_N.map((n, k) => `fxN += (fxNoise(uFarCoastQ[${k}] + vGeo * ${f7(COAST_INV[k])}, ${COAST_MASK}, ${11 + k}) * 2.0 - 1.0) * ${f4(COAST_AMP[k])} * (1.0 - smoothstep(${f4(COAST_M[k] * 0.3)}, ${f4(COAST_M[k] * 0.7)}, cFoot));`).join('\n    ')}
    cFarD += fxTaper * fxN;
  }
  // Past the near field the distance to the shore comes from how much land
  // the map's own mips see: 20 km, 39 km and 78 km boxes, each opening up
  // where the one below it is clear. The water shading reads this for its
  // shelf and for the deep blue beyond.
  if (cFarD < -600.0) {
    float fxN2 = fxLandNear(cFarUv, 2.0), fxN3 = fxLandNear(cFarUv, 3.0), fxN4 = fxLandNear(cFarUv, 4.0);
    float fxCoarse = 10000.0 + 19000.0 * (1.0 - fxN2) + 39000.0 * (1.0 - fxN3) * (1.0 - fxN2)
      + 78000.0 * (1.0 - fxN4) * (1.0 - fxN3);
    cFarD = min(cFarD, -mix(600.0, fxCoarse, smoothstep(600.0, 1400.0, -cFarD)));
  }
}
#endif
`;

// Land cover and its detail. This replaces the Cape's own detail block on a
// roaming patch: scrub, grass and rock layers picked by what the day map's
// colour says grows there, with procedural structure in world space so the
// ground still reads at a kilometre, where a 5 km texel is a smear.
//
// Every lattice below is a power of two of metres and its hash repeats over
// the frame's own fold period (64 km fine, 2048 km coarse), so folding the
// frame moves nothing.
export const FAR_LAND = /* glsl */`
{
  vec3 fxLab = wLinToOklab(max(cBase, vec3(1e-5)));
  float fxL = fxLab.x;
  float fxC = length(fxLab.yz);
  float fxHue = degrees(atan(fxLab.z, fxLab.y));
  float fxGreen = smoothstep(92.0, 112.0, fxHue) * (1.0 - smoothstep(150.0, 185.0, fxHue));
  // Vegetation from lightness and hue: dark green is forest, pale tan is bare.
  float fxVeg = clamp((0.80 - fxL) / 0.42, 0.0, 1.0) * mix(0.5, 1.0, fxGreen);
  float fxSnow = smoothstep(0.80, 0.87, fxL) * (1.0 - smoothstep(0.022, 0.048, fxC));
  float fxSandW = smoothstep(0.745, 0.83, fxL) * (1.0 - fxSnow);
  float fxForest = smoothstep(0.60, 0.82, fxVeg) * (1.0 - fxSnow);
  float fxOpen = (1.0 - fxForest) * (1.0 - fxSnow) * (1.0 - fxSandW);
  float fxLush = smoothstep(0.18, 0.52, fxVeg);          // grass rather than scrub
  float fxBare = 1.0 - smoothstep(0.05, 0.22, fxVeg);    // rock and bare soil

  // Nobody farms the middle of a rainforest. What grows for eighty kilometres
  // around comes off the map's own mip chain, and it vetoes the field pattern
  // where the country is forest, which is what a big river's smear through it
  // would otherwise turn into.
  float fxWide = 1.0;
#ifdef USE_MAP
  {
    vec3 fxWLab = wLinToOklab(max(textureLod(map, cFarUv, 4.0).rgb, vec3(1e-5)));
    float fxWHue = degrees(atan(fxWLab.z, fxWLab.y));
    float fxWGreen = smoothstep(92.0, 112.0, fxWHue) * (1.0 - smoothstep(150.0, 185.0, fxWHue));
    float fxWVeg = clamp((0.80 - fxWLab.x) / 0.42, 0.0, 1.0) * mix(0.5, 1.0, fxWGreen);
    fxWide = 1.0 - smoothstep(0.60, 0.76, fxWVeg);
  }
#endif
  // Country-scale variation: which stretches are farmed and which are left
  // wild, in patches of a few tens of kilometres.
  float fxRegion = fxNoise(cTPB * (1.0 / 16384.0), 127, 61);
  float fxTill = fxWide * smoothstep(0.40, 0.56, fxRegion) * (1.0 - smoothstep(52.0, 60.0, abs(cFarLat)))
    * smoothstep(0.04, 0.13, fxVeg) * (1.0 - fxSandW) * (1.0 - fxSnow) * (1.0 - smoothstep(0.5, 0.8, fxForest));

  vec2 fxSlope = vec2(0.0);
  vec3 fxTint = vec3(1.0);
  float fxRough = 0.93;

  // Ground layers tiled at 32 m, the second sample turned a quarter turn so
  // the tile never repeats visibly and the pattern still folds cleanly.
  vec2 fxUv = cTP * (1.0 / 32.0);
  vec2 fxUv2 = vec2(-fxUv.y, fxUv.x) + 0.317;
  float fxPick = smoothstep(0.35, 0.65, fxNoise(cTP * (1.0 / 64.0), 1023, 71));
  vec3 fxScrub = mix(texture2D(uDetailA, fxUv).rgb, texture2D(uDetailA, fxUv2).rgb, fxPick);
  vec3 fxGrass = mix(texture2D(uDetailB, fxUv).rgb, texture2D(uDetailB, fxUv2).rgb, fxPick);
  vec3 fxRockT = mix(texture2D(uDetailD, fxUv * 0.7).rgb, texture2D(uDetailD, fxUv2 * 0.7).rgb, fxPick);
  vec3 fxSandT = mix(texture2D(uDetailC, fxUv * 1.7).rgb, texture2D(uDetailC, fxUv2 * 1.7).rgb, fxPick);
  vec3 fxMScrub = max(textureLod(uDetailA, vec2(0.5), 12.0).rgb, vec3(0.02));
  vec3 fxMGrass = max(textureLod(uDetailB, vec2(0.5), 12.0).rgb, vec3(0.02));
  vec3 fxMRock = max(textureLod(uDetailD, vec2(0.5), 12.0).rgb, vec3(0.02));
  vec3 fxMSand = max(textureLod(uDetailC, vec2(0.5), 12.0).rgb, vec3(0.02));
  vec3 fxTex = mix(mix(fxRockT / fxMRock, fxScrub / fxMScrub, smoothstep(0.02, 0.2, fxVeg)), fxGrass / fxMGrass, fxLush);
  // Bare sand keeps only a whisper of the coast layer's grain.
  fxTex = mix(fxTex, mix(vec3(1.0), fxSandT / fxMSand, 0.4), fxSandW);
  fxTex = clamp(fxTex, vec3(0.45), vec3(1.85));
  float fxNear = 1.0 - smoothstep(1.0, 3.5, cFoot);

  // Forest: crowns on a 16 m lattice, lit on top and dark in the gaps.
  if (fxForest > 0.01) {
    vec2 fxTo;
    vec4 fxCr = fxCell(cTP * (1.0 / 16.0), 4095, 83, fxTo);
    float fxR = 0.34 + 0.30 * fxCr.z;
    float fxU = clamp(fxCr.x / fxR, 0.0, 1.0);
    float fxDome = 1.0 - fxU * fxU;
    float fxFade = 1.0 - smoothstep(2.5, 8.0, cFoot);
    float fxCanopy = fxNoise(cTPB * (1.0 / 1024.0), 2047, 87);
    float fxShade = mix(0.66, 1.10, fxDome) * (0.86 + 0.28 * fxCr.z);
    fxTint = mix(fxTint, vec3(mix(1.0, fxShade, fxFade) * (0.82 + 0.36 * fxCanopy)), fxForest);
    // A crown is a squat paraboloid: flat on top, steep at its rim.
    vec2 fxOut = fxCr.x > 1e-3 ? -fxTo / fxCr.x : vec2(0.0);
    fxSlope -= fxOut * (0.85 * fxU * step(fxU, 0.999)) * fxForest * fxFade;
    fxRough = mix(fxRough, 0.97, fxForest);
  }

  // Open country: fields where it is farmed, scrub and grass where it is not.
  if (fxOpen > 0.01) {
    vec2 fxToB;
    vec4 fxBlock = fxCell(cTPB * (1.0 / 1024.0), 2047, 101, fxToB);
    float fxAng = fxBlock.z * 3.14159;
    vec2 fxDir = vec2(cos(fxAng), sin(fxAng));
    vec2 fxLoc = -fxToB * 1024.0;
    float fxAcross = dot(fxLoc, vec2(-fxDir.y, fxDir.x));
    float fxAlong = dot(fxLoc, fxDir);
    float fxW = mix(55.0, 170.0, fract(fxBlock.z * 7.3));
    float fxLen = mix(180.0, 520.0, fract(fxBlock.z * 3.1));
    float fxSi = floor(fxAcross / fxW);
    float fxSj = floor(fxAlong / fxLen + fract(fxSi * 0.618) * 0.7);
    ivec2 fxPid = ivec2(int(fxSi) & 255, int(fxSj) & 255);
    int fxSeed = int(fxBlock.z * 4096.0);
    float fxCrop = fxHash(fxPid, fxSeed);
    float fxCrop2 = fxHash(fxPid, fxSeed + 3);
    // Crop tones go through Oklab, so a golden field stays golden instead of
    // dragging its green through grey.
    vec3 fxFieldLab = fxLab;
    fxFieldLab.x *= 1.0 + (fxCrop - 0.5) * 0.30;
    fxFieldLab.y += (fxCrop2 - 0.5) * 0.020 - 0.004;
    fxFieldLab.z += (fxCrop - 0.45) * 0.030;
    vec3 fxField = wOklabToLin(fxFieldLab) / max(cBase, vec3(1e-4));
    // Tracks and hedges along the parcel edges, gone once a pixel spans them.
    float fxEdgeA = min(fract(fxAcross / fxW), 1.0 - fract(fxAcross / fxW)) * fxW;
    float fxEdgeB = min(fract(fxAlong / fxLen), 1.0 - fract(fxAlong / fxLen)) * fxLen;
    float fxEdge = min(min(fxEdgeA, fxEdgeB), (fxBlock.y - fxBlock.x) * 512.0);
    float fxLine = (1.0 - smoothstep(2.0, 4.5 + cFoot, fxEdge)) * (1.0 - smoothstep(1.5, 5.0, cFoot));
    float fxFieldW = fxTill * (1.0 - smoothstep(28.0, 70.0, cFoot));
    fxTint = mix(fxTint, clamp(fxField, vec3(0.55), vec3(1.7)), fxFieldW * fxOpen);
    fxTint *= mix(vec3(1.0), vec3(mix(0.86, 1.12, fxCrop2)), fxLine * fxFieldW * fxOpen);
    // Rows, and soil that never dries evenly: the only structure a field has
    // left once you are close enough to land in it.
    float fxRow = 0.5 + 0.5 * sin(fxAcross * 6.2832 / mix(3.0, 7.5, fxCrop));
    fxTint *= mix(1.0, 0.94 + 0.12 * fxRow, fxFieldW * fxOpen * (1.0 - smoothstep(0.7, 2.2, cFoot)));
    fxTint *= mix(1.0, 0.92 + 0.16 * fxNoise(cTPB * (1.0 / 64.0), 32767, 119), fxFieldW * fxOpen * (1.0 - smoothstep(6.0, 18.0, cFoot)));
    // Wild country: shrub and grass in patches from tens of metres to a
    // kilometre, which is what keeps a hillside from reading as paint.
    float fxPatch = fxNoise(cTPB * (1.0 / 64.0), 32767, 111) * 0.3
      + fxNoise(cTPB * (1.0 / 256.0), 8191, 113) * 0.4 + fxNoise(cTPB * (1.0 / 1024.0), 2047, 117) * 0.3;
    fxTint *= mix(1.0, 0.78 + 0.44 * fxPatch, (1.0 - 0.7 * fxFieldW) * fxOpen * (1.0 - 0.35 * fxBare));
    fxRough = mix(fxRough, 0.95, fxOpen);
  }

  // Bare rock and gravel: eroded ridges, and the dark varnish of a desert
  // pavement.
  if (fxBare * fxOpen > 0.02) {
    vec3 fxN1 = fxNoiseD(cTPB * (1.0 / 256.0), 8191, 131);
    vec3 fxN2 = fxNoiseD(cTP * (1.0 / 64.0), 1023, 137);
    float fxFadeA = 1.0 - smoothstep(40.0, 110.0, cFoot);
    float fxFadeB = 1.0 - smoothstep(10.0, 28.0, cFoot);
    fxSlope += (fxN1.yz * (7.0 / 256.0) * fxFadeA + fxN2.yz * (1.1 / 64.0) * fxFadeB) * fxBare * fxOpen;
    fxTint *= mix(1.0, 0.86 + 0.30 * fxN1.x, fxBare * fxOpen * fxFadeA);
    fxRough = mix(fxRough, 0.89, fxBare * fxOpen);
  }

  // Sand seas: transverse dunes with a steep lee face, riding on draa a
  // kilometre and a half apart. Wave numbers are whole parts of the fold
  // period, so the ridges survive a fold unmoved.
  if (fxSandW > 0.01) {
    vec2 fxK1 = vec2(252.0, 146.0) / 65536.0;      // 225 m apart, running 30 degrees
    vec2 fxK2 = vec2(26.0, -35.0) / 65536.0;       // draa, 1.5 km apart
    float fxWob = (fxNoise(cTP * (1.0 / 256.0), 255, 151) - 0.5) * 0.55
      + (fxNoise(cTP * (1.0 / 64.0), 1023, 153) - 0.5) * 0.22;
    float fxP2 = dot(cTP, fxK2);
    float fxDraa = sin(6.2832 * fxP2);
    float fxT = fract(dot(cTP, fxK1) + fxWob);
    float fxH = fxT < 0.78 ? smoothstep(0.0, 0.78, fxT) : 1.0 - smoothstep(0.78, 1.0, fxT);
    float fxDh = fxT < 0.78 ? 6.0 * fxT * (0.78 - fxT) / 0.474552 : -6.0 * (fxT - 0.78) * (1.0 - fxT) / 0.010648;
    float fxAmp = 26.0 * (0.45 + 0.55 * smoothstep(-0.5, 0.6, fxDraa));
    float fxFadeA = 1.0 - smoothstep(30.0, 90.0, cFoot);
    float fxFadeB = 1.0 - smoothstep(180.0, 520.0, cFoot);
    fxSlope += (fxDh * fxAmp * fxK1 * fxFadeA + cos(6.2832 * fxP2) * 138.2 * fxK2 * fxFadeB) * fxSandW;
    // Crests are winnowed pale, the interdune floors coarser and darker.
    fxTint *= mix(1.0, 0.94 + 0.13 * fxH + 0.05 * fxDraa, fxSandW * fxFadeB);
    fxRough = mix(fxRough, 0.84, fxSandW);
  }

  // Snow and ice: sastrugi combed out along the wind, and very little else.
  // An ice sheet is the flattest ground on Earth.
  if (fxSnow > 0.01) {
    vec2 fxAx = normalize(uFarWind + vec2(1e-4, 0.0));
    vec2 fxPerp = vec2(-fxAx.y, fxAx.x);
    vec3 fxN = fxNoiseD(vec2(dot(cTP, fxAx) / 26.0, dot(cTP, fxPerp) / 5.0), 2047, 167);
    float fxFade = 1.0 - smoothstep(1.2, 4.0, cFoot);
    fxSlope += (fxN.y / 26.0 * fxAx + fxN.z / 5.0 * fxPerp) * 0.5 * fxFade * fxSnow;
    fxTint *= mix(1.0, 0.985 + 0.03 * fxN.x, fxSnow * fxFade);
    fxRough = mix(fxRough, 0.74, fxSnow);
  }

  // What a 5 km texel cannot hold: slow variation at one to eight kilometres,
  // faded out before orbit so the globe still matches.
  float fxMacro = fxNoise(cTPB * (1.0 / 2048.0), 1023, 191) * 0.55 + fxNoise(cTPB * (1.0 / 4096.0), 511, 193) * 0.45;
  float fxMacroW = (1.0 - smoothstep(40000.0, 90000.0, cView)) * (1.0 - 0.6 * fxSnow);
  fxTint *= mix(1.0, 0.90 + 0.20 * fxMacro, fxMacroW);

  cLand *= mix(vec3(1.0), fxTex, 0.75 * fxNear * (1.0 - fxSnow) * (1.0 - fxForest));
  cLand *= fxTint;
  // The day map is a picture of the Earth, not a reflectance map: its
  // rainforest sits near 3 per cent where real canopy is nearer 12, and under
  // a physical light that reads as a hole. Close in, where the crowns are what
  // you are looking at, the darkest ground is lifted toward that; by a few
  // tens of kilometres it is the map's own tone again, so orbit still matches
  // the planet behind it.
  float fxDark = 1.0 - smoothstep(0.035, 0.105, dot(cBase, vec3(0.2126, 0.7152, 0.0722)));
  cLand *= 1.0 + 1.25 * fxDark * (1.0 - smoothstep(3000.0, 30000.0, cView));
  cRough = fxRough;
  cLSlope = fxSlope;
  // Beaches belong to sand and soil, not to an ice coast.
  cBeach *= mix(1.0, 0.15, fxSnow);
  cSand *= mix(vec3(1.0), clamp(fxTex, vec3(0.6), vec3(1.5)), 0.5 * fxNear);
  cFarDbg = vec3(fxForest * 0.9 + 0.2 * fxLush, fxOpen * (0.3 + 0.5 * fxTill), fxSandW * 0.8 + fxSnow);
}
`;
